import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import AdminShell from "../AdminShell";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-dashboard.css";

/**
 * RAdminDashboard — /admin/dashboard
 *
 * "Tour Dashboard" redesign per design_handoff_admin_dashboard/Admin
 * Dashboard.dc.html: mine-tour statistics as the hero content plus the
 * visitor-origin map; the old orders-centric launchpad content is demoted
 * to a small store-snapshot strip (module navigation lives in AdminShell).
 *
 * Data:
 *   - GET /api/admin/bookings/live-dashboard/{today} → today's slot cards
 *   - GET /api/settings/tour-schedule/public          → Mon/Fri rule note
 *   - GET /api/admin/stats/tours?start&end            → KPIs + charts
 *   - GET /api/admin/stats/visitor-origins?start&end  → map + top origins
 *   - GET /api/config/maps                            → Maps JS browser key
 *   - GET /api/admin/orders/stats                     → store snapshot
 *   - GET /api/admin/database/export                  → DB backup (kept
 *     verbatim from the previous dashboard: 403 super-admin gate, JSON
 *     error parsing, empty-blob guard, anchor download)
 */

/* ---------------------------------------------------------------- utils -- */

const ADELAIDE_TZ = "Australia/Adelaide";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(
    amount
  );

const formatCurrencyWhole = (amount) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(amount);

const formatNum = (n) => Number(n || 0).toLocaleString("en-AU");

/* "2026-07-13" for today in Adelaide (en-CA renders ISO order) */
const adelaideTodayISO = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: ADELAIDE_TZ });

/* minutes since midnight, Adelaide */
const adelaideNowMinutes = () => {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: ADELAIDE_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const h = Number(parts.find((p) => p.type === "hour")?.value || 0);
  const m = Number(parts.find((p) => p.type === "minute")?.value || 0);
  return h * 60 + m;
};

const isoAddDays = (iso, delta) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

const RANGES = {
  "7d": { label: "Last 7 days", days: 7 },
  "30d": { label: "Last 30 days", days: 30 },
  "90d": { label: "Last 90 days", days: 90 },
  ytd: { label: "Year to date", days: null },
};

const rangeDates = (rangeKey) => {
  const end = adelaideTodayISO();
  const start =
    rangeKey === "ytd"
      ? `${end.slice(0, 4)}-01-01`
      : isoAddDays(end, -(RANGES[rangeKey].days - 1));
  return { start, end };
};

const SLOT_TIMES = ["10:00", "14:00", "16:00"];
const SLOT_LABELS = { "10:00": "10:00 AM", "14:00": "2:00 PM", "16:00": "4:00 PM" };
const SLOT_MINUTES = { "10:00": 600, "14:00": 840, "16:00": 960 };

/* percent delta line vs the previous window, e.g. "▲ 14% vs prev" */
const deltaLine = (curr, prev) => {
  if (!prev || prev <= 0 || curr == null) return null;
  const pct = Math.round(((curr - prev) / prev) * 100);
  if (pct === 0) return { text: "— level with prev", dir: "flat" };
  return {
    text: `${pct > 0 ? "▲" : "▼"} ${Math.abs(pct)}% vs prev`,
    dir: pct > 0 ? "up" : "down",
  };
};

/* Bucket the per-day series for the stacked chart: daily / 2-day / weekly /
   monthly depending on the visible span (per the design's bucketing note). */
const bucketSeries = (byDay) => {
  if (!byDay || byDay.length === 0) return [];
  const days = byDay.length;
  const monthName = (iso) =>
    new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-AU", {
      timeZone: "UTC",
      month: "short",
    });
  if (days > 130) {
    const months = new Map();
    byDay.forEach((d) => {
      const k = d.date.slice(0, 7);
      const b = months.get(k) || { label: monthName(d.date), adults: 0, children: 0 };
      b.adults += d.adults;
      b.children += d.children;
      months.set(k, b);
    });
    return Array.from(months.values());
  }
  const step = days <= 14 ? 1 : days <= 40 ? 2 : 7;
  const out = [];
  for (let i = 0; i < days; i += step) {
    const chunk = byDay.slice(i, i + step);
    const first = chunk[0].date;
    const d = new Date(`${first}T12:00:00Z`);
    const label =
      step === 1
        ? d.toLocaleDateString("en-AU", { timeZone: "UTC", weekday: "short", day: "numeric" })
        : `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
    out.push({
      label,
      adults: chunk.reduce((s, x) => s + x.adults, 0),
      children: chunk.reduce((s, x) => s + x.children, 0),
    });
  }
  return out;
};

/* Warm-paper Google Maps styling (matches the design's #F4F1EA map surface) */
const MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#e6ddcb" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b665e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f4f1ea" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#d4c7ad" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#f4f1ea" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9a958c" }] },
];

/* one-time Maps JS loader */
let mapsLoaderPromise = null;
const loadGoogleMaps = (key) => {
  if (window.google && window.google.maps) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise((resolve, reject) => {
    const cb = "__umoonaMapsReady";
    window[cb] = () => resolve();
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key
    )}&loading=async&callback=${cb}`;
    s.async = true;
    s.onerror = () => reject(new Error("Google Maps failed to load"));
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
};

const LAST_BACKUP_KEY = "adminLastDbBackup";

const formatBackupStamp = (ts) =>
  new Date(ts)
    .toLocaleString("en-AU", {
      timeZone: ADELAIDE_TZ,
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());

/* ----------------------------------------------------------- component -- */

const RAdminDashboard = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [range, setRange] = useState("90d");
  const [stats, setStats] = useState(null);
  const [origins, setOrigins] = useState(null);
  const [live, setLive] = useState(null);
  const [scheduleCfg, setScheduleCfg] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [mapsCfg, setMapsCfg] = useState(null);
  const [mapMode, setMapMode] = useState("pins");
  const [mapError, setMapError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(() => {
    const stored = Number(localStorage.getItem(LAST_BACKUP_KEY));
    return stored > 0 ? stored : null;
  });

  const todayISO = adelaideTodayISO();

  /* ------------------------------------------------------------ fetches -- */

  const fetchToday = useCallback(async () => {
    try {
      const [liveRes, schedRes, ordersRes] = await Promise.all([
        adminFetch(`/api/admin/bookings/live-dashboard/${todayISO}`, { navigate }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/tour-schedule/public`),
        adminFetch("/api/admin/orders/stats", { navigate }),
      ]);
      if (liveRes.ok) setLive(await liveRes.json());
      if (schedRes.ok) setScheduleCfg(await schedRes.json());
      if (ordersRes.ok) setOrderStats(await ordersRes.json());
    } catch (err) {
      if (!err.isAuthError) console.error("Dashboard today-strip error:", err);
    }
  }, [navigate, todayISO]);

  const fetchRangeStats = useCallback(
    async (rangeKey) => {
      const { start, end } = rangeDates(rangeKey);
      setRangeLoading(true);
      try {
        const [statsRes, originsRes] = await Promise.all([
          adminFetch(`/api/admin/stats/tours?start_date=${start}&end_date=${end}`, {
            navigate,
          }),
          adminFetch(
            `/api/admin/stats/visitor-origins?start_date=${start}&end_date=${end}`,
            { navigate }
          ),
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (originsRes.ok) setOrigins(await originsRes.json());
      } catch (err) {
        if (!err.isAuthError) {
          toast.error("Failed to load tour statistics");
          console.error("Dashboard stats error:", err);
        }
      } finally {
        setRangeLoading(false);
        setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    fetchToday();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/maps`)
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then(setMapsCfg)
      .catch(() => setMapsCfg({ enabled: false }));
  }, [fetchToday]);

  useEffect(() => {
    fetchRangeStats(range);
  }, [range, fetchRangeStats]);

  /* -------------------------------------------------------------- backup -- */

  const handleDatabaseBackup = async () => {
    try {
      setBackupLoading(true);
      toast.info("⤓ Preparing database backup… download will start shortly");

      const response = await adminFetch("/api/admin/database/export", { navigate });

      if (response.status === 403) {
        toast.error("🔒 Access Denied: Super admin role required for database export", 4000);
        return;
      }
      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = "Failed to export database";
        try {
          errorMsg = JSON.parse(errorText).detail || errorMsg;
        } catch (e) {
          errorMsg = errorText || errorMsg;
        }
        throw new Error(errorMsg);
      }

      const blob = await response.blob();
      if (blob.size === 0) throw new Error("Export returned empty file");

      const filename = `database_backup_${new Date().toISOString().split("T")[0]}.json`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
      const stamp = Date.now();
      localStorage.setItem(LAST_BACKUP_KEY, String(stamp));
      setLastBackup(stamp);
      toast.success(`✅ Database backup downloaded — ${filename} (${sizeMB} MB)`, 4000);
    } catch (err) {
      if (err.isAuthError) return;
      console.error("Backup error:", err);
      toast.error(`❌ Failed to backup database: ${err.message}`, 4000);
    } finally {
      setBackupLoading(false);
    }
  };

  /* -------------------------------------------------------- derived data -- */

  const totals = stats?.totals;
  const rangeLabel = RANGES[range].label;

  const kpis = useMemo(() => {
    if (!totals) return [];
    const prev = stats?.previous_period || {};
    const gd = deltaLine(totals.guests, prev.guests);
    const rd = deltaLine(totals.revenue, prev.revenue);
    return [
      {
        label: "Guests",
        value: formatNum(totals.guests),
        icon: "adm-stat-icon--teal",
        sub: gd?.text,
        subCls: gd?.dir === "down" ? "adm-stat-sub--ember" : "adm-stat-sub--teal",
      },
      {
        label: "Bookings",
        value: formatNum(totals.bookings),
        icon: "adm-stat-icon--blue",
        sub: `${formatNum(totals.online_bookings)} online · ${formatNum(
          totals.walkin_bookings
        )} walk-in`,
        subCls: "adm-stat-sub--blue",
      },
      {
        label: "Ticket revenue",
        value: formatCurrencyWhole(totals.revenue),
        icon: "adm-stat-icon--ember",
        sub: rd?.text,
        subCls: rd?.dir === "down" ? "adm-stat-sub--ember" : "adm-stat-sub--teal",
      },
      {
        label: "Capacity used",
        value: totals.capacity_pct != null ? `${Math.round(totals.capacity_pct)}%` : "—",
        icon: "adm-stat-icon--amber",
        sub: `${formatNum(totals.open_slots)} tours ran`,
        subCls: "adm-stat-sub--amber",
      },
      {
        label: "Check-in rate",
        value: totals.checkin_rate != null ? `${Math.round(totals.checkin_rate)}%` : "—",
        icon: "adm-stat-icon--teal",
        sub: "past tours · online bookings",
        subCls: "adm-stat-sub--teal",
      },
    ];
  }, [totals, stats]);

  const chartData = useMemo(() => bucketSeries(stats?.by_day), [stats]);
  const chartTickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const sourceSplit = useMemo(() => {
    if (!totals) return [];
    return [
      { name: "Online", value: totals.online_guests, color: "#1BAB88" },
      { name: "Walk-in", value: totals.walkin_guests, color: "#2596C9" },
      { name: "Coach group", value: totals.coach_guests, color: "#D5622B" },
    ].filter((s) => s.value > 0);
  }, [totals]);
  const sourceTotal = sourceSplit.reduce((s, x) => s + x.value, 0);

  const timeSlots = stats?.by_time_slot || {};
  const timeMax = Math.max(1, ...SLOT_TIMES.map((t) => timeSlots[t] || 0));

  const ticketMix = useMemo(() => {
    const m = stats?.by_ticket_type;
    if (!m) return null;
    const total = m.adult_guests + m.child_guests + m.family_guests;
    if (!total) return null;
    const pct = (v) => `${Math.round((v / total) * 100)}%`;
    return {
      total,
      rows: [
        { label: "Adult", hint: "· $16", n: m.adult_guests, color: "#1BAB88", pct: pct(m.adult_guests) },
        { label: "Child", hint: "· $9", n: m.child_guests, color: "#18BFC6", pct: pct(m.child_guests) },
        { label: "Family package", hint: "· $36–61", n: m.family_guests, color: "#F4A06B", pct: pct(m.family_guests) },
      ],
    };
  }, [stats]);

  /* ---- today strip ---- */

  const todayInfo = useMemo(() => {
    if (!live) return null;
    const nowMin = adelaideNowMinutes();
    const weekday = new Date(`${todayISO}T12:00:00Z`).getUTCDay(); // 1=Mon 5=Fri
    const isMonFri = weekday === 1 || weekday === 5;
    const restrictionOn = Boolean(scheduleCfg?.restrict_monday_friday) && isMonFri;

    const nextTime = SLOT_TIMES.find(
      (t) => !live.time_slots?.[t]?.blocked && SLOT_MINUTES[t] > nowMin
    );

    const slots = SLOT_TIMES.map((t) => {
      const s = live.time_slots?.[t] || {};
      const used = s.total_people || 0;
      const online = s.online_tickets || 0;
      const walkin = s.walkin_tickets || 0;
      const capacity = s.capacity?.total || 30;
      let state = "open";
      let pill = "OPEN";
      if (s.blocked) {
        state = "closed";
        pill = restrictionOn ? `CLOSED · ${weekday === 1 ? "MON" : "FRI"}` : "CLOSED";
      } else if (t === nextTime) {
        state = "next";
        pill = "NEXT UP";
      } else if (SLOT_MINUTES[t] <= nowMin) {
        state = "done";
        pill = "DEPARTED";
      } else if (restrictionOn && t !== "16:00") {
        pill = "OPEN · OVERRIDE";
      }
      return {
        time: t,
        label: SLOT_LABELS[t],
        state,
        pill,
        used,
        online,
        walkin,
        capacity,
        left: Math.max(0, capacity - used),
      };
    });

    // Online check-ins (walk-ins have no individual check-in)
    let onlineGuests = 0;
    let checkedGuests = 0;
    SLOT_TIMES.forEach((t) => {
      (live.time_slots?.[t]?.customers || []).forEach((c) => {
        onlineGuests += c.guest_count || 0;
        if (c.checked_in) checkedGuests += c.guest_count || 0;
      });
    });

    let countdown = null;
    if (nextTime) {
      const mins = SLOT_MINUTES[nextTime] - nowMin;
      countdown = `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
    }

    const overrideOpen =
      restrictionOn && slots.some((s) => s.time !== "16:00" && s.state !== "closed");

    return {
      slots,
      restrictionOn,
      overrideOpen,
      checkedGuests,
      onlineGuests,
      countdown,
      dateLabel: new Date(`${todayISO}T12:00:00Z`).toLocaleDateString("en-AU", {
        timeZone: "UTC",
        weekday: "long",
        day: "numeric",
        month: "short",
      }),
    };
  }, [live, scheduleCfg, todayISO]);

  /* ---- origins / map ---- */

  const originRows = useMemo(() => {
    const list = origins?.origins || [];
    const totalBookings = list.reduce((s, o) => s + o.bookings, 0) || 1;
    return list.map((o, i) => ({
      ...o,
      rank: i + 1,
      region: [o.state, o.country === "Australia" ? "AUSTRALIA" : o.country]
        .filter(Boolean)
        .join(" · ")
        .toUpperCase(),
      pct: `${Math.round((o.bookings / totalBookings) * 100)}%`,
    }));
  }, [origins]);

  const topOrigins = originRows.slice(0, 8);
  const restOrigins = originRows.slice(8);
  const intlOrigins = originRows.filter((o) => o.country && o.country !== "Australia");

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);

  useEffect(() => {
    if (!mapsCfg?.enabled || !origins) return;
    let cancelled = false;
    loadGoogleMaps(mapsCfg.browser_key)
      .then(() => {
        if (cancelled || !mapDivRef.current) return;
        const g = window.google.maps;
        if (!mapRef.current) {
          mapRef.current = new g.Map(mapDivRef.current, {
            center: { lat: -27, lng: 134 },
            zoom: 4,
            styles: MAP_STYLES,
            backgroundColor: "#F4F1EA",
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "cooperative",
          });
        }
        const map = mapRef.current;

        overlaysRef.current.forEach((o) => o.setMap(null));
        overlaysRef.current = [];

        const list = origins.origins || [];
        if (list.length === 0) return;

        if (mapMode === "pins") {
          const info = new g.InfoWindow();
          list.forEach((o, i) => {
            const isTop = i === 0;
            const marker = new g.Marker({
              map,
              position: { lat: o.lat, lng: o.lng },
              title: `${o.label} — ${o.bookings} bookings · ${o.guests} guests`,
              icon: {
                path: g.SymbolPath.CIRCLE,
                scale: 11 + Math.min(14, o.bookings * 2.2),
                fillColor: isTop ? "#F15A24" : "#1BAB88",
                fillOpacity: 0.95,
                strokeColor: "#ffffff",
                strokeWeight: 2.5,
              },
              label: {
                text: String(o.bookings),
                color: "#ffffff",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: "800",
                fontSize: "12px",
              },
            });
            marker.addListener("click", () => {
              info.setContent(
                `<div style="font-family:Mulish,sans-serif;font-size:13px">` +
                  `<strong style="font-family:Montserrat,sans-serif">${o.label}</strong><br/>` +
                  `${o.bookings} bookings · ${o.guests} guests</div>`
              );
              info.open({ map, anchor: marker });
            });
            overlaysRef.current.push(marker);
          });
        } else {
          // Heat mode: layered translucent circles weighted by guest count
          // (avoids the deprecated visualization HeatmapLayer)
          list.forEach((o) => {
            const base = 45000 + o.guests * 9000;
            [
              { r: base, color: "#1BAB88", op: 0.1 },
              { r: base * 0.62, color: "#F4A06B", op: 0.22 },
              { r: base * 0.3, color: "#F15A24", op: 0.38 },
            ].forEach((ring) => {
              overlaysRef.current.push(
                new g.Circle({
                  map,
                  center: { lat: o.lat, lng: o.lng },
                  radius: ring.r,
                  fillColor: ring.color,
                  fillOpacity: ring.op,
                  strokeWeight: 0,
                })
              );
            });
          });
        }

        const bounds = new g.LatLngBounds();
        list.forEach((o) => bounds.extend({ lat: o.lat, lng: o.lng }));
        // Always keep an Australia-wide frame; fit data inside it
        bounds.extend({ lat: -43.6, lng: 113.6 });
        bounds.extend({ lat: -10.7, lng: 153.6 });
        map.fitBounds(bounds, 30);
      })
      .catch((err) => {
        console.error("Maps init failed:", err);
        setMapError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [mapsCfg, origins, mapMode]);

  /* --------------------------------------------------------------- render -- */

  const rangeChips = (
    <div className="dash-range">
      <span className="dash-range-label">Range</span>
      {Object.keys(RANGES).map((k) => (
        <button
          key={k}
          type="button"
          className={`dash-range-chip${range === k ? " is-active" : ""}`}
          onClick={() => setRange(k)}
        >
          {k === "ytd" ? "YTD" : k}
        </button>
      ))}
    </div>
  );

  return (
    <AdminShell
      active="dashboard"
      pageClass="pg-admin-dashboard"
      eyebrow="Mine Tours · Overview"
      title="Tour Dashboard"
      actions={rangeChips}
    >
      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading dashboard...
        </div>
      ) : (
        <>
          {/* -------- Today · operational strip -------- */}
          {todayInfo && (
            <section className="dash-section">
              <div className="dash-eyebrow-row">
                <span className="dash-eyebrow dash-eyebrow--terra">
                  Today · {todayInfo.dateLabel}
                </span>
                {todayInfo.restrictionOn && (
                  <span className="dash-rule-note">
                    <span className="dash-rule-dot" />
                    MON/FRI 4 PM RESTRICTION ACTIVE
                    {todayInfo.overrideOpen && " · RE-OPENED VIA OVERRIDE"}
                  </span>
                )}
              </div>
              <div className="dash-slots">
                {todayInfo.slots.map((s) => (
                  <div key={s.time} className={`dash-slot dash-slot--${s.state}`}>
                    <div className="dash-slot-head">
                      <span className="dash-slot-time">{s.label}</span>
                      <span className={`dash-slot-pill dash-slot-pill--${s.state}`}>
                        {s.state === "next" && <span className="dash-pill-dot" />}
                        {s.pill}
                      </span>
                    </div>
                    {s.state === "closed" ? (
                      <div className="dash-slot-closed">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <rect x="5" y="11" width="14" height="9" rx="2" />
                          <path d="M8 11V8a4 4 0 018 0v3" />
                        </svg>
                        <span>Not running today</span>
                      </div>
                    ) : (
                      <div className="dash-slot-count">
                        <span className="dash-slot-big">{s.used}</span>
                        <span className="dash-slot-cap">/ {s.capacity} guests</span>
                      </div>
                    )}
                    <div className="dash-slot-bar">
                      {s.state !== "closed" && (
                        <>
                          <span
                            className="dash-slot-bar-online"
                            style={{ width: `${(s.online / s.capacity) * 100}%` }}
                          />
                          <span
                            className="dash-slot-bar-walkin"
                            style={{ width: `${(s.walkin / s.capacity) * 100}%` }}
                          />
                        </>
                      )}
                    </div>
                    <div className="dash-slot-foot">
                      {s.state === "closed" ? (
                        <span>SEASONAL 4 PM-ONLY RULE</span>
                      ) : (
                        <>
                          <span>
                            {s.online} ONLINE · {s.walkin} WALK-IN
                          </span>
                          <span>{s.left} LEFT</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                <div className="dash-ops">
                  <div className="dash-ops-stats">
                    <div>
                      <div className="dash-ops-label">Checked in</div>
                      <div className="dash-ops-value">
                        {todayInfo.checkedGuests}
                        <span> / {todayInfo.onlineGuests}</span>
                      </div>
                    </div>
                    <div>
                      <div className="dash-ops-label">Next tour</div>
                      <div className="dash-ops-value">
                        {todayInfo.countdown || "—"}
                        {todayInfo.countdown && <span> hrs</span>}
                      </div>
                    </div>
                  </div>
                  <Link to="/admin/live-dashboard" className="dash-ops-btn">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="2.5" />
                      <path d="M7.5 7.5a6 6 0 000 9M16.5 7.5a6 6 0 010 9" />
                    </svg>
                    Open Live Dashboard
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* -------- Tour statistics KPI row -------- */}
          <section className="dash-section">
            <div className="dash-eyebrow-row">
              <span className="dash-eyebrow dash-eyebrow--teal">
                Tour statistics · {rangeLabel}
                {rangeLoading && <span className="dash-eyebrow-loading"> · updating…</span>}
              </span>
              <Link className="adm-link" to="/admin/analytics">
                Full analytics →
              </Link>
            </div>
            <div className="dash-kpis">
              {kpis.map((kpi) => (
                <div className="adm-stat" key={kpi.label}>
                  <div className="adm-stat-top">
                    <span className="adm-stat-label">{kpi.label}</span>
                    <span className={`adm-stat-icon ${kpi.icon}`} />
                  </div>
                  <div className="adm-stat-value">{kpi.value}</div>
                  {kpi.sub && <div className={`adm-stat-sub ${kpi.subCls}`}>{kpi.sub}</div>}
                </div>
              ))}
            </div>
          </section>

          {/* -------- Charts: guests per day + source donut -------- */}
          <section className="dash-charts">
            <div className="adm-card dash-chart-card">
              <div className="dash-chart-head">
                <div className="adm-card-title">Guests per day</div>
                <div className="dash-legend">
                  <span className="dash-legend-item">
                    <span className="dash-legend-swatch" style={{ background: "#1BAB88" }} />
                    ADULTS
                  </span>
                  <span className="dash-legend-item">
                    <span className="dash-legend-swatch" style={{ background: "#F4A06B" }} />
                    CHILDREN
                  </span>
                </div>
              </div>
              <div className="dash-chart-sub">
                {rangeLabel} · Australia/Adelaide
              </div>
              {chartData.length === 0 ? (
                <div className="dash-empty">No guests recorded in this range yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} margin={{ top: 6, right: 4, left: -18, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#EAE4D8" />
                    <XAxis
                      dataKey="label"
                      interval={chartTickInterval}
                      tick={{ fontFamily: "Space Mono, monospace", fontSize: 9.5, fill: "#9A958C" }}
                      axisLine={{ stroke: "#EAE4D8" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontFamily: "Space Mono, monospace", fontSize: 9.5, fill: "#9A958C" }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(27,171,136,.06)" }}
                      contentStyle={{
                        fontFamily: "Mulish, sans-serif",
                        fontSize: 12.5,
                        border: "1px solid #E5DFD3",
                        borderRadius: 9,
                      }}
                    />
                    <Bar dataKey="adults" name="Adults" stackId="g" fill="#1BAB88" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="children" name="Children" stackId="g" fill="#F4A06B" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="adm-card dash-chart-card">
              <div className="adm-card-title">Guest source</div>
              <div className="dash-chart-sub">{rangeLabel}</div>
              {sourceTotal === 0 ? (
                <div className="dash-empty">No guests in this range yet.</div>
              ) : (
                <>
                  <div className="dash-donut">
                    <PieChart width={180} height={180}>
                      <Pie
                        data={sourceSplit}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={53}
                        outerRadius={79}
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                      >
                        {sourceSplit.map((s) => (
                          <Cell key={s.name} fill={s.color} />
                        ))}
                      </Pie>
                    </PieChart>
                    <div className="dash-donut-center">
                      <span className="dash-donut-total">{formatNum(sourceTotal)}</span>
                      <span className="dash-donut-cap">Guests</span>
                    </div>
                  </div>
                  <div className="dash-source-legend">
                    {sourceSplit.map((s) => (
                      <div key={s.name} className="dash-source-row">
                        <span className="dash-source-name">
                          <span className="dash-legend-swatch" style={{ background: s.color }} />
                          {s.name}
                        </span>
                        <span className="dash-source-val">
                          {formatNum(s.value)}{" "}
                          <span>· {Math.round((s.value / sourceTotal) * 100)}%</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* -------- Time popularity + ticket mix -------- */}
          <section className="dash-charts dash-charts--even">
            <div className="adm-card dash-chart-card">
              <div className="adm-card-title">Tour-time popularity</div>
              <div className="dash-chart-sub">Guests by slot · {rangeLabel}</div>
              <div className="dash-timebars">
                {SLOT_TIMES.map((t, i) => {
                  const val = timeSlots[t] || 0;
                  const colors = ["#1BAB88", "#2596C9", "#D5622B"];
                  return (
                    <div key={t}>
                      <div className="dash-timebar-head">
                        <span>{SLOT_LABELS[t]}</span>
                        <span>{formatNum(val)}</span>
                      </div>
                      <div className="dash-timebar-track">
                        <span
                          style={{
                            width: `${(val / timeMax) * 100}%`,
                            background: colors[i],
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="adm-card dash-chart-card">
              <div className="dash-chart-head">
                <div className="adm-card-title">Ticket-type mix</div>
                {totals?.avg_party_online != null && (
                  <span className="dash-chart-note">avg party {totals.avg_party_online}</span>
                )}
              </div>
              <div className="dash-chart-sub">Share of guests · {rangeLabel}</div>
              {!ticketMix ? (
                <div className="dash-empty">No ticketed guests in this range yet.</div>
              ) : (
                <>
                  <div className="dash-mixbar">
                    {ticketMix.rows.map((r) => (
                      <span
                        key={r.label}
                        style={{ width: r.pct, background: r.color }}
                      />
                    ))}
                  </div>
                  <div className="dash-mix-rows">
                    {ticketMix.rows.map((r) => (
                      <div key={r.label} className="dash-source-row">
                        <span className="dash-source-name">
                          <span className="dash-legend-swatch" style={{ background: r.color }} />
                          {r.label} <em>{r.hint}</em>
                        </span>
                        <span className="dash-source-val">
                          {formatNum(r.n)} <span>· {r.pct}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* -------- Visitor-origin map -------- */}
          <section className="dash-section">
            <div className="adm-card dash-map-card">
              <div className="dash-map-head">
                <div>
                  <div className="dash-eyebrow dash-eyebrow--ocean">
                    Where visitors travel from
                  </div>
                  <div className="dash-map-title">Visitor origins</div>
                </div>
                <div className="dash-range dash-range--inline">
                  <button
                    type="button"
                    className={`dash-range-chip${mapMode === "pins" ? " is-active" : ""}`}
                    onClick={() => setMapMode("pins")}
                  >
                    Pins
                  </button>
                  <button
                    type="button"
                    className={`dash-range-chip${mapMode === "heatmap" ? " is-active" : ""}`}
                    onClick={() => setMapMode("heatmap")}
                  >
                    Heatmap
                  </button>
                </div>
              </div>
              <div className="dash-map-grid">
                <div className="dash-map-canvas">
                  {mapsCfg?.enabled && !mapError ? (
                    <div ref={mapDivRef} className="dash-map-el" />
                  ) : (
                    <div className="dash-map-fallback">
                      <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="#9A958C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 20l-6-3V4l6 3 6-3 6 3v13l-6-3-6 3z" />
                        <path d="M9 7v13M15 4v13" />
                      </svg>
                      <span>
                        {mapError
                          ? "Map failed to load — origins are listed on the right."
                          : "Google Maps key not configured — origins are listed on the right."}
                      </span>
                    </div>
                  )}
                  {originRows.length > 0 && (
                    <div className="dash-map-legend">
                      {mapMode === "pins"
                        ? "PIN SIZE = BOOKINGS · FLAME = TOP ORIGIN"
                        : "HOT SPOTS WEIGHTED BY GUEST COUNT"}
                    </div>
                  )}
                  {intlOrigins.length > 0 && (
                    <div className="dash-map-intl">
                      {intlOrigins.length} INTERNATIONAL ·{" "}
                      {intlOrigins[0].label.toUpperCase()}
                      {intlOrigins.length > 1 ? " +" : ""}
                    </div>
                  )}
                </div>

                <div className="dash-origins">
                  <div className="dash-origins-head">
                    <span>Top origins</span>
                    <span className="dash-origins-count">
                      {formatNum(origins?.totals?.bookings_with_origin || 0)} BOOKINGS
                    </span>
                  </div>
                  {topOrigins.length === 0 ? (
                    <div className="dash-empty">
                      No origin data in this range yet — the "visiting from" field
                      has captured origins since 5 Jul 2026.
                    </div>
                  ) : (
                    <div className="dash-origin-list">
                      {topOrigins.map((o) => (
                        <div key={`${o.label}-${o.state}`} className="dash-origin-row">
                          <span
                            className="dash-origin-rank"
                            style={{ background: o.rank === 1 ? "#F15A24" : "#1BAB88" }}
                          >
                            {o.rank}
                          </span>
                          <span className="dash-origin-place">
                            <span className="dash-origin-city">{o.label}</span>
                            <span className="dash-origin-region">{o.region}</span>
                          </span>
                          <span className="dash-origin-nums">
                            <span className="dash-origin-bkg">{o.bookings} bkg</span>
                            <span className="dash-origin-guests">
                              {o.guests} guests · {o.pct}
                            </span>
                          </span>
                        </div>
                      ))}
                      {restOrigins.length > 0 && (
                        <div className="dash-origin-more">
                          <span className="dash-origin-rank dash-origin-rank--more">+</span>
                          <span>
                            {formatNum(
                              restOrigins.reduce((s, o) => s + o.bookings, 0)
                            )}{" "}
                            more across {restOrigins.length} places
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="dash-origins-note">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#E0A92B" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 8h.01M11 12h1v4h1" />
                    </svg>
                    <span>
                      Online bookings only — walk-ins &amp; coach groups don't capture
                      origin. Field live since 5&nbsp;Jul&nbsp;2026.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* -------- Secondary: store snapshot + DB backup -------- */}
          <section className="dash-secondary">
            <div className="adm-card">
              <div className="dash-chart-head">
                <div>
                  <div className="adm-card-title">Store snapshot</div>
                  <div className="dash-chart-sub">SEE ORDERS &amp; ANALYTICS FOR DETAIL</div>
                </div>
                <Link className="adm-link" to="/admin/orders">
                  Orders →
                </Link>
              </div>
              <div className="dash-store-tiles">
                <div className="dash-store-tile">
                  <div className="dash-store-label">New orders</div>
                  <div className="dash-store-value">
                    {orderStats ? formatNum(orderStats.new_orders) : "—"}
                  </div>
                  <div className="dash-store-sub dash-store-sub--warn">
                    awaiting fulfilment
                  </div>
                </div>
                <div className="dash-store-tile">
                  <div className="dash-store-label">Store revenue</div>
                  <div className="dash-store-value">
                    {orderStats ? formatCurrencyWhole(orderStats.total_revenue) : "—"}
                  </div>
                  <div className="dash-store-sub dash-store-sub--blue">
                    {orderStats ? `${formatCurrency(orderStats.today_revenue)} today` : ""}
                  </div>
                </div>
                <div className="dash-store-tile">
                  <div className="dash-store-label">Shipped</div>
                  <div className="dash-store-value">
                    {orderStats ? formatNum(orderStats.shipped_orders) : "—"}
                  </div>
                  <div className="dash-store-sub dash-store-sub--teal">
                    {orderStats && orderStats.total_orders > 0
                      ? `${Math.round(
                          (orderStats.shipped_orders / orderStats.total_orders) * 100
                        )}% of all orders`
                      : ""}
                  </div>
                </div>
              </div>
            </div>

            <div className="adm-card dash-db-card">
              <div className="adm-card-title">Database</div>
              <p className="adm-card-text">
                Export a full backup of products, orders, bookings and settings as
                JSON. Super-admin only.
              </p>
              <button
                type="button"
                className="adm-btn adm-btn--dark"
                onClick={handleDatabaseBackup}
                disabled={backupLoading}
              >
                {backupLoading ? "⤓ Exporting…" : "⤓ Full Database Backup"}
              </button>
              <div className="adm-card-foot">
                {lastBackup
                  ? `Last backup · ${formatBackupStamp(lastBackup)}`
                  : "Last backup · —"}
              </div>
            </div>
          </section>
        </>
      )}
    </AdminShell>
  );
};

export default RAdminDashboard;
